import React, { useState } from "react";
import { DropdownItem } from "reactstrap";
import { 
    Breadcrumb, 
    ConfirmDialog,
    NeoCard,
    NeoTabs,
    NeoInput,
    NeoLoader,
    NeoBadge,
    NeoDropdown
} from "../../../components/Common";
import useGoals from "./hooks/useGoals";
import GoalsForm from "./components/GoalsForm";
import { METRIC_GROUP_LABELS, METRIC_GROUP_ICONS } from "../../../constants/performance";
import { GOAL_TEMPLATES, GOAL_TEMPLATE_OPTIONS } from "../../../constants/goalTemplates";

/**
 * GoalsPage — Página para definir metas de performance por grupo de cargo e por mês.
 * Migrado para o Neo Design System (Taça Outono).
 */
const GoalsPage = () => {
    document.title = "Metas | NeoApp - Sistema de Gestão";

    const {
        goalsData,
        loading,
        saving,
        selectedPeriod,
        setSelectedPeriod,
        periodOptions,
        handleSave,
        applyTemplate,
        applyTemplateYearly,
    } = useGoals();

    const [activeTab, setActiveTab] = useState("gerente");

    const groupTabs = [
        { id: "gerente", label: METRIC_GROUP_LABELS.gerente || "Academia", icon: METRIC_GROUP_ICONS.gerente || "mdi-shield-crown" },
        { id: "professor", label: METRIC_GROUP_LABELS.professor || "Professor", icon: METRIC_GROUP_ICONS.professor || "mdi-school" },
        { id: "consultor", label: METRIC_GROUP_LABELS.consultor || "Consultor(a)", icon: METRIC_GROUP_ICONS.consultor || "mdi-handshake" },
    ];

    const currentData = goalsData[activeTab] || {};

    const handleApplyTemplate = async (templateKey) => {
        const template = GOAL_TEMPLATES[templateKey];
        if (template?.groups) {
            const currentYear = selectedPeriod.split("-")[0];
            const wantsReplication = await ConfirmDialog({
                title: "Replicar para todo o ano?",
                text: `Deseja replicar e salvar automaticamente o Template "${template.label}" em TODOS os meses do ano de ${currentYear}?\n\n*(Se clicar em apenas preencher, ele não será salvo e valerá somente para o mês ativo)*`,
                confirmButtonText: "Sim, replicar no ano todo",
                cancelButtonText: "Apenas preencher o mês",
                acceptColor: "primary"
            });

            if (wantsReplication) {
                applyTemplateYearly(template.groups);
            } else {
                applyTemplate(template.groups);
            }
        }
    };

    // Ações do cabeçalho do NeoCard
    const headerActions = (
        <div className="d-flex align-items-center gap-2 flex-wrap">
            {/* Seletor de Período */}
            <NeoInput
                type="select"
                value={selectedPeriod}
                onChange={e => setSelectedPeriod(e.target.value)}
                style={{ minWidth: "180px" }}
                options={periodOptions}
            />

            {/* Dropdown de Templates */}
            <NeoDropdown
                label="Templates"
                icon="mdi-file-document-outline"
                variant="secondary"
            >
                <DropdownItem header className="font-size-11 text-uppercase fw-bold text-muted border-bottom pb-2 mb-2">
                    Pré-configurações
                </DropdownItem>
                {GOAL_TEMPLATE_OPTIONS.map(tmpl => (
                    <DropdownItem
                        key={tmpl.value}
                        onClick={() => handleApplyTemplate(tmpl.value)}
                        className="py-2 rounded-2"
                    >
                        <div className="d-flex align-items-center gap-2">
                            <i className={`mdi ${tmpl.icon} font-size-18`} style={{ color: tmpl.color }}></i>
                            <div>
                                <div className="fw-bold font-size-13 text-dark text-start">{tmpl.label}</div>
                                <div className="text-muted font-size-11 text-start">{tmpl.description}</div>
                            </div>
                        </div>
                    </DropdownItem>
                ))}
            </NeoDropdown>
        </div>
    );

    return (
        <React.Fragment>
            <Breadcrumb title="Metas" />

            <NeoCard
                title="Metas de Performance"
                subtitle="Defina as metas absolutas e pesos das métricas por grupo de cargo."
                headerIcon="mdi-bullseye-arrow"
                headerActions={headerActions}
            >
                {/* Abas por grupo */}
                <NeoTabs
                    tabs={groupTabs}
                    activeTab={activeTab}
                    onChangeTab={setActiveTab}
                />

                {loading ? (
                    <NeoLoader message="Carregando metas..." />
                ) : (
                    <div className="pt-2">
                        {/* Barra de Status */}
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2">
                                <i className={`mdi ${groupTabs.find(t => t.id === activeTab)?.icon} text-brand-primary font-size-18`}></i>
                                <h6 className="mb-0 fw-bold text-brand-primary font-size-14" style={{ fontFamily: "Outfit, sans-serif" }}>
                                    Configuração: {groupTabs.find(t => t.id === activeTab)?.label}
                                </h6>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <NeoBadge color="secondary">
                                    {Object.keys(currentData.weights || {}).length} métricas
                                </NeoBadge>
                                <NeoBadge color="primary">
                                    <i className="mdi mdi-calendar me-1"></i>
                                    {periodOptions.find(p => p.value === selectedPeriod)?.label || selectedPeriod}
                                </NeoBadge>
                            </div>
                        </div>

                        {/* Avisos */}
                        {currentData.isDefault && !currentData.isTemplate && (
                            <div className="alert alert-info rounded-3 py-2 font-size-12 mb-3 shadow-sm d-flex align-items-center gap-2 border-0 bg-light-soft text-primary">
                                <i className="mdi mdi-information-outline font-size-16 text-primary"></i>
                                <span>
                                    {currentData.isFromBase
                                        ? "Usando metas padrão (nenhuma meta específica salva para este mês)."
                                        : "Nenhuma meta definida ainda. Use um Template como ponto de partida ou preencha manualmente."
                                    }
                                </span>
                            </div>
                        )}
                        {currentData.isTemplate && (
                            <div className="alert alert-warning rounded-3 py-2 font-size-12 mb-3 shadow-sm d-flex align-items-center gap-2 border-0 bg-light-soft text-warning">
                                <i className="mdi mdi-check-circle font-size-16 text-warning"></i>
                                <span>
                                    Template aplicado. Revise os valores e clique em <strong>Salvar Metas</strong>.
                                </span>
                            </div>
                        )}

                        {/* Formulário */}
                        <GoalsForm
                            metricGroup={activeTab}
                            goals={currentData.goals || {}}
                            weights={currentData.weights || {}}
                            onSave={handleSave}
                            saving={saving}
                        />
                    </div>
                )}
            </NeoCard>
        </React.Fragment>
    );
};

export default GoalsPage;
